import type {
	LayerMaskConfiguration,
	LayerSpecification,
	MapboxMapController,
	SourceSpecification,
	TileSourceSpecification,
	WeatherLayerOptions,
} from '@xweather/mapsgl'
import { capSeverityFromAlertTypeCode, EuropeGlobalAlertTypeCodeSchema, USCanadaAlertTypeCodeSchema } from '@xweather/protect-types'
import { type ReactNode, useEffect, useMemo } from 'react'
import { I18n } from '../../components/I18n.tsx'
import type { IconName } from '../../components/Icon.tsx'
import type { MeasurementTypeUnit } from './../../data/measurementTypes.ts'
import { useSubscription } from '../../data/service.ts'
import { type UserPreferences, usePreferences } from '../../data/userProfile.ts'
import { env } from '../../env.ts'
import { useFlags } from '../../launchdarkly.ts'
import { type ThemeMode, useLayerInput } from '../../states.ts'
import { localizeAlertTypeName } from '../alerts/utils.tsx'
import { getConversionFunction } from './../measurements/convertMeasurements.tsx'
import { useTimeline } from '../timeline/timelineState.ts'
import {
	type CustomLayerProps,
	type DoubleBufferedLayerId,
	LightningAlertRings,
	LightningDensityLayer,
	LightningGridAnimated,
	LightningGridLayer,
	LightningPointAnimated,
	LightningPointLayer,
	LTZCircleLayer,
	LTZLineLayer,
	LTZPolygonLayer,
} from './customLayers.tsx'
import { type LayerInputs, ToggleButtonInput } from './inputs.tsx'
import { eventsPerYearLongUnit, useIsMapZoomBelow } from './layerUtils.ts'
import { colorscales, LIGHTNING_ZOOM_BOUNDARY, LTZ_DETAILED_MIN_ZOOM, legendLabels, legendRanges } from './legendConstants.tsx'

export type LayerView = MapsglLayerView | CustomLayerView | SwitchLayerView | HybridLayerView | ComboLayerView

interface GenericLayerView {
	id: LayerViewId
	title: ReactNode
	LayerInputComponent?: () => ReactNode
}

interface MapsglLayerView extends GenericLayerView {
	type: 'mapsgl'
	animatable: boolean
	layers: { id: LayerId; options?: () => Partial<WeatherLayerOptions> | undefined }[]
	layerStyle: LayerStyle
}

interface CustomLayerView extends GenericLayerView {
	type: 'custom'
	animatable: boolean
	layers: {
		id: LayerId | DoubleBufferedLayerId
		render: (props: CustomLayerProps) => ReactNode
		animate?: (props: CustomLayerProps) => ReactNode
	}[]
	layerStyle: LayerStyle
}

interface SwitchLayerView extends GenericLayerView {
	type: 'switch'
	layerViews: Exclude<LayerView, SwitchLayerView>[]
}

interface HybridLayerView extends GenericLayerView {
	type: 'hybrid'
	animatable: boolean
	layers: {
		id: LayerId
		source: Partial<SourceSpecification>
		config: Omit<LayerSpecification, 'source' | 'mask'> & { source: string }
		/** Applied via `ctrl.setMask` after the layer is added. Mask layer ids are weather layer codes, not map ids. */
		mask?: LayerMaskConfiguration
	}[]
	layerStyle: LayerStyle
}

interface ComboLayerView extends GenericLayerView {
	type: 'combo'
	animatable: boolean
	layerViews: Exclude<LayerView, SwitchLayerView | ComboLayerView>[]
	layerStyle: LayerStyle
}

export type LayerId = Exclude<(typeof layerOrder)[number]['id'], ''>
export type LayerMapId = Exclude<(typeof layerOrder)[number]['mapId'], ''> | 'building-number-label'
export type LayerMaskId = 'radar-rain' | 'radar-mix' | 'radar-snow'
export type LayerViewId =
	| LayerId
	| 'precip'
	| 'pressure-msl'
	| 'road-temperature'
	| 'road-weather-summary'
	| 'lightning-realtime'
	| 'lightning-threatzone'
	| 'hail-threats'
	| 'fires'
	| 'tropical-cyclones'
	| 'extreme'

export type MappedFeature = { id: string; name: string; color: string; icon: IconName }

export type ResolvedLayerStyle = {
	format?: (v: number) => ReactNode
	title?: ReactNode
	unit?: MeasurementTypeUnit
	useShouldHide?: () => boolean
	updateMapsglLayer?: (ctrl: MapboxMapController) => void
} & (
	| { type: 'gradient'; colorStops: (string | number)[]; min: number; max: number; ticks?: { value: number; position: number }[] }
	| { type: 'category'; categories: { id: string; name: ReactNode }[]; colorStops: (string | number)[] }
	| { type: 'icon'; icons: { id: string; name: ReactNode; element: ReactNode }[]; stackDirection?: 'row' | 'column' }
	| { type: 'step'; steps: (number | string)[]; colorStops: (string | number)[] }
	| {
			type: 'original'
			legendId: LayerId
			useFeatureMapper: () => (feature: Record<string, unknown>) => MappedFeature
	  }
	| { type: 'multiple'; styles: (Exclude<ResolvedLayerStyle, { type: 'multiple' }> & { id: string })[] }
	| { type: 'empty' }
)

export type LayerStyle = (
	pref: UserPreferences,
	theme: ThemeMode,
	inputs: LayerInputs,
	mode?: ThemeMode,
	isNewColor?: boolean,
) => ResolvedLayerStyle

export const layerOrder = [
	{ id: 'temperatures', mapId: 'conditions.temperature.fill' },
	{ id: 'feels-like', mapId: 'conditions.feels_like.fill' },
	{ id: 'dew-points', mapId: 'conditions.dew_point.fill' },
	{ id: 'satellite-infrared-color', mapId: 'conditions.satellite-infrared-color.fill' },
	{ id: 'air-quality-index', mapId: 'air_quality.index.fill' },
	{ id: 'alerts-outline', mapId: 'severe.alerts.line' },
	{ id: 'alerts', mapId: 'severe.alerts.fill' },
	{ id: 'wind-gusts', mapId: 'conditions.wind_gust.fill' },
	{ id: 'lightning-density', mapId: 'lightning-density' },
	{ id: 'fires-perimeter', mapId: 'climate.fire_obs.perimeter' },
	{ id: 'snow', mapId: 'conditions.snow.fill' },
	{ id: 'snow-depth', mapId: 'conditions.snow_depth.fill' },
	{ id: 'visibility', mapId: 'conditions.visibility.fill' },
	{ id: '', mapId: 'hillshade-relief' },
	{ id: 'radar', mapId: 'conditions.radar.fill' },
	{ id: 'fires-obs', mapId: 'climate.fire_obs.point' },
	{ id: 'pressure-msl-contour', mapId: 'conditions.pressure_msl.contour' },
	{ id: 'froad-weather-summary-us', mapId: 'road_weather.summary.us.primary.fill' },
	{ id: 'froad-weather-summary-europe', mapId: 'road_weather.summary.europe.primary.fill' },
	{ id: 'froad-weather-summary-australia', mapId: 'road_weather.summary.australia.primary.fill' },
	{ id: 'froad-weather-summary-japan', mapId: 'road_weather.summary.japan.primary.fill' },
	{ id: 'froad-weather-summary-new-zealand', mapId: 'road_weather.summary.new_zealand.primary.fill' },
	{ id: 'froad-weather-temperature-us', mapId: 'road_weather.temperature.us.primary.fill' },
	{ id: 'froad-weather-temperature-europe', mapId: 'road_weather.temperature.europe.primary.fill' },
	{ id: 'power-lines-lightning-density', mapId: 'power-lines-lightning-density' },
	{ id: 'power-lines', mapId: 'base.power_lines.line' },
	{ id: 'tropical-cyclones-forecast-error-cones', mapId: 'tropical-cyclones-forecast-error-cones' },
	{ id: 'ltz-polygon-1', mapId: 'ltz-polygon' },
	{ id: 'ltz-polygon-2', mapId: 'ltz-polygon' },
	{ id: 'lightning-realtime-grid-1', mapId: 'lightning-realtime-grid-1' },
	{ id: 'lightning-realtime-grid-2', mapId: 'lightning-realtime-grid-2' },
	{ id: 'hail-threats-polygons', mapId: 'severe.hail.threats.fill' },
	{ id: 'lightning-alert-rings-pulse', mapId: 'lightning-alert-rings-pulse' },
	{ id: 'lightning-alert-rings', mapId: 'lightning-alert-rings' },
	{ id: 'wind-particles', mapId: 'conditions.wind_speed.particle' },
	{ id: 'lightning-realtime-point-1', mapId: 'lightning-realtime-point-1' },
	{ id: 'lightning-realtime-point-2', mapId: 'lightning-realtime-point-2' },
	{ id: 'tropical-cyclones-track-lines-archive', mapId: 'tropical.track_archive.line' },
	{ id: 'tropical-cyclones-forecast-lines', mapId: 'tropical.forecast_active.line' },
	{ id: 'tropical-cyclones-positions', mapId: 'tropical.positions_active.circle' },
	{ id: 'tropical-cyclones-track-points-archive', mapId: 'tropical.track_archive.circle' },
	{ id: 'tropical-cyclones-forecast-points', mapId: 'tropical.forecast_active.circle' },
	{ id: 'ltz-centroid-1', mapId: 'ltz-centroid-1' },
	{ id: 'ltz-centroid-2', mapId: 'ltz-centroid-2' },
	{ id: 'ltz-velocity-1', mapId: 'ltz-velocity-1' },
	{ id: 'ltz-velocity-2', mapId: 'ltz-velocity-2' },
	{ id: 'hail-threats-tracks', mapId: 'severe.hail.threats.line' },
	{ id: 'hail-threats-points', mapId: 'severe.hail.threats.circle' },
	{ id: '', mapId: 'building-number-label' },
	{ id: 'asset-geometry-polygon', mapId: 'asset-geometry-polygon' },
	{ id: 'asset-geometry-linestring', mapId: 'asset-geometry-linestring' },
	{ id: 'asset-geometry-circle', mapId: 'asset-geometry-circle' },
	{ id: 'locations', mapId: 'locations' },
	{ id: 'locations-icon', mapId: 'locations-icon' },
	{ id: 'locations-cluster', mapId: 'locations-cluster' },
	{ id: 'locations-count', mapId: 'locations-count' },
	{ id: 'asset-coordinate-labels', mapId: 'asset-coordinate-labels' },
	{ id: 'distance-points-layer', mapId: 'distance-points-layer' },
	{ id: 'distance-line-layer', mapId: 'distance-line-layer' },
] as const

export const getMapId = (id: (typeof layerOrder)[number]['id']) => layerOrder.find((layer) => layer.id === id)?.mapId ?? id
export const getLayerId = (mapId: (typeof layerOrder)[number]['mapId']) =>
	layerOrder.find((layer) => layer.mapId === mapId)?.id ?? undefined

export const segmentColorscale = (colorscale: Record<number, string>) => {
	const colors = Object.entries(colorscale)
		.filter(([key]) => Number(key) !== 1)
		.map(([_, value]) => value)
	const total = colors.length

	const new_colorscale = colors.reduce(
		(acc, color, idx) => {
			const key = Number((idx / total).toFixed(2))
			if (key === 1) return acc
			acc[key] = color
			return acc
		},
		{} as Record<number, string>,
	)

	const lastColor = colors.at(-1)
	if (!lastColor) throw new Error('colorscale is empty')

	new_colorscale[1] = lastColor

	return interpolateColorscaleFlat(new_colorscale)
}

// colors: [red, green, blue] -> { 0: red, 0.5: green, 1: blue }
export const colorscaleFromLinearColors = (colors: string[]) =>
	Object.fromEntries(colors.map((color, index) => [index / (colors.length - 1), color]))

export const categorizeColorscale = (categories: { color: string | undefined }[]) => {
	const total = categories.length

	const colorscale = categories.reduce(
		(acc, category, idx) => {
			const key = Number((idx / total).toFixed(2))
			acc[key] = category.color
			return acc
		},
		{} as Record<number, string | undefined>,
	)
	if (categories.length > 0) {
		colorscale[1] = categories.at(-1)?.color
	}

	return interpolateColorscaleFlat(colorscale)
}

// 3 evenly-spaced ticks for ranges too narrow for the default 5-tick split; `mid` defaults to (min+max)/2 but can be overridden.
const minMidMaxTicks = (min: number, max: number, mid: number = (min + max) / 2) => [
	{ value: min, position: 0 },
	{ value: mid, position: 0.5 },
	{ value: max, position: 1 },
]

export const interpolateColorscale = (
	colorscale: Record<number, string | undefined>,
	min: number = 0,
	max: number = 1,
	convert?: (val: number) => number,
) =>
	Object.keys(colorscale)
		.sort((a, b) => Number(a) - Number(b))
		.flatMap((percent): [number, string][] => {
			const rawValue = min + Number(percent) * (max - min)
			const convertedValue = convert ? convert(rawValue) : rawValue
			const value = Math.round(convertedValue * 10_000) / 10_000
			const color = colorscale[Number(percent)]
			if (!color) return []
			return [[value, color]]
		})

export const interpolateColorscaleFlat = (...params: Parameters<typeof interpolateColorscale>) => interpolateColorscale(...params).flat()

export const interpolateColorscaleLog = (colorscale: Record<number, string | undefined>, min: number, max: number) => {
	const percents = Object.keys(colorscale).map(Number)
	const curve = 1 / Math.min(...percents.filter((percent) => percent > 0 && percent < 1), 1) - 1
	if (curve <= 0) return { colorStops: interpolateColorscaleFlat(colorscale, min, max) }
	const log = Math.log1p(curve)
	const position = (value: number) => Math.log1p(((value - min) / (max - min)) * curve) / log

	const minSpacing = 0.12
	const step = 2 ** Math.max(1, Math.ceil(minSpacing / (1 - position(max / 2))))
	const ticks = [{ value: min, position: 0 }]
	for (let value = 2 ** Math.floor(Math.log2(max)); position(value) >= minSpacing; value /= step) {
		if (1 - position(value) >= minSpacing) ticks.splice(1, 0, { value, position: position(value) })
	}
	ticks.push({ value: max, position: 1 })

	return {
		colorStops: interpolateColorscaleFlat(
			Object.fromEntries(percents.map((percent) => [Math.log1p(percent * curve) / log, colorscale[percent]])),
			min,
			max,
		),
		ticks,
		format: (value: number) => (value >= 10 || value === 0 ? value.toFixed(0) : String(+value.toFixed(3))),
	}
}

const reverseColorscale = (colorscale: Record<number, string>) => {
	const entries = Object.entries(colorscale)
		.map(([k, v]) => [Number(k), v] as [number, string])
		.sort((a, b) => a[0] - b[0])

	const reversed: Record<number, string> = {}
	for (let i = 0; i < entries.length; i++) {
		const entry = entries[i]
		const reverseEntry = entries[entries.length - 1 - i]

		if (!entry || !reverseEntry) continue

		const [key] = entry
		const [, value] = reverseEntry

		reversed[key] = value
	}

	return reversed
}

const powerLinesLtgDensity: LayerView = {
	id: 'power-lines-lightning-density',
	title: <I18n i18nKey={($) => $.layers.views['power-lines-ltg']} />,
	type: 'hybrid',
	animatable: true,
	layers: [
		{
			id: 'power-lines-lightning-density',
			source: {
				type: 'vector',
				minZoom: 1,
				maxZoom: 5,
				url: `${env.LTG_CONTOUR_LAYER}/world-density-mvt/{z}/{x}/{y}.pbf`,
			} as TileSourceSpecification,
			config: {
				source: 'lightning-density-source',
				sourceLayer: 'dg',
				type: 'fill',
				paint: {
					fill: {
						sort: 'density',
						color: (data) => {
							const density = data?.density
							if (typeof density !== 'number') return '#FFFFFF00'
							return (
								interpolateColorscale(
									colorscales['power-lines-lightning-density'],
									...legendRanges['power-lines-lightning-density'].KILOMETRES,
								).find(([maxDensity]) => density <= maxDensity)?.[1] ?? '#FFFFFF00'
							)
						},
					},
				},
			},
			mask: { layers: [{ id: 'power-lines' }] },
		},
	],
	layerStyle: (pref) => {
		const colorscale = colorscales['power-lines-lightning-density']
		const [min, max] = legendRanges['power-lines-lightning-density'][pref.unitsDistance]
		const { colorStops, format, ticks } = interpolateColorscaleLog(colorscale, min, max)
		return {
			type: 'gradient',
			unit: eventsPerYearLongUnit[pref.unitsDistance],
			min,
			max,
			format,
			ticks,
			colorStops,
			updateMapsglLayer: () => {},
		}
	},
}
const powerLines = {
	type: 'switch',
	id: 'power-lines',
	title: <I18n i18nKey={($) => $.layers.views['power-lines']} />,
	layerViews: [
		{
			id: 'power-lines',
			title: <I18n i18nKey={($) => $.layers.views['power-lines-only']} />,
			type: 'mapsgl',
			animatable: true,
			layers: [{ id: 'power-lines' }],
			layerStyle: () => ({ type: 'empty' }),
		},
		powerLinesLtgDensity,
	],
} as const satisfies LayerView

const govtAlerts: LayerView = {
	id: 'alerts',
	type: 'mapsgl',
	layers: [
		{
			id: 'alerts-outline',
			options: () => ({ data: { maxZoom: 7 } }),
		},
		{
			id: 'alerts',
			options: () => {
				const alertTypeCodes = [...USCanadaAlertTypeCodeSchema.options, ...EuropeGlobalAlertTypeCodeSchema.options].filter(
					(code) => capSeverityFromAlertTypeCode(code) === 'EXTREME' || capSeverityFromAlertTypeCode(code) === 'SEVERE',
				)
				return {
					data: { maxZoom: 7 },
					paint: { stroke: { thickness: 0.1, opacity: 1 } },
					filter: ['in', ['get', 'VTEC'], ['literal', alertTypeCodes]],
				}
			},
		},
	],
	title: <I18n i18nKey={($) => $.layers.views.alerts} />,
	animatable: true,
	layerStyle: (_pref) => ({
		type: 'original',
		legendId: 'alerts',
		useFeatureMapper: () => {
			const preferences = usePreferences()
			return (feature: Record<string, unknown>) => {
				const aFeature = feature as { COLOR: string; VTEC: string; ADVISORY: string }
				const severity = capSeverityFromAlertTypeCode(aFeature.VTEC)
				return {
					id: aFeature.VTEC,
					name: localizeAlertTypeName(aFeature.VTEC, preferences.data.language, aFeature.ADVISORY),
					color: aFeature.COLOR,
					icon: severity === 'EXTREME' || severity === 'SEVERE' ? 'Circle' : 'DotCircle',
				}
			}
		},
		updateMapsglLayer: () => {},
		useShouldHide: () => useIsMapZoomBelow(5.4),
	}),
}
const wildfires: LayerView = {
	id: 'fires',
	type: 'mapsgl',
	layers: [
		{ id: 'fires-obs', options: () => ({ data: { maxZoom: 7 } }) },
		{ id: 'fires-perimeter', options: () => ({ data: { maxZoom: 7 } }) },
	],
	title: <I18n i18nKey={($) => $.layers.views.fires} />,
	animatable: true,
	layerStyle: (_pref) => ({
		type: 'category',
		categories: legendLabels.fires,
		colorStops: categorizeColorscale(legendLabels.fires),
		updateMapsglLayer: (ctrl) => {
			const fireColor = (data: { report: { perContained: number; startDateISO: string } }) => {
				const { report } = data
				const startDate = new Date(report.startDateISO)
				if (report.perContained === 100) return legendLabels.fires[2].color
				// less than a day old = new
				else if (Date.now() - startDate.getTime() < 1000 * 60 * 60 * 24) return legendLabels.fires[0].color
				else return legendLabels.fires[1].color
			}
			ctrl.setPaintProperty(getMapId('fires-obs'), 'fill.color', fireColor)
		},
	}),
}
const tropicalCyclones: LayerView = {
	id: 'tropical-cyclones',
	type: 'mapsgl',
	layers: [
		{
			id: 'tropical-cyclones-forecast-error-cones',
			options: () => ({
				data: { maxZoom: 7 },
				filter: ['>=', ['map-time'], ['-', ['now'], 60]],
			}),
		},
		{ id: 'tropical-cyclones-track-lines-archive', options: () => ({ data: { maxZoom: 7 } }) },
		{
			id: 'tropical-cyclones-forecast-lines',
			options: () => ({
				data: { maxZoom: 7 },
				filter: ['<=', ['get', 'timestamp'], ['map-time']],
			}),
		},
		{ id: 'tropical-cyclones-positions', options: () => ({ data: { maxZoom: 7 } }) },
		{ id: 'tropical-cyclones-track-points-archive', options: () => ({ data: { maxZoom: 7 } }) },
		{
			id: 'tropical-cyclones-forecast-points',
			options: () => ({
				data: { maxZoom: 7 },
				filter: ['<=', ['get', 'timestamp'], ['map-time']],
			}),
		},
	],
	title: <I18n i18nKey={($) => $.layers.views['tropical-cyclones']} />,
	animatable: true,
	layerStyle: (_pref) => ({
		type: 'icon',
		icons: legendLabels['tropical-cyclones'],
		updateMapsglLayer: (ctrl) => {
			const cycloneColor = (data: { details: { stormCat: string } }) => {
				const { details } = data
				const stormCat = details.stormCat.toUpperCase()
				const categoryMap: Record<string, number> = {
					LO: 0,
					TD: 1,
					TS: 2,
					H1: 3,
					H2: 4,
					H3: 5,
					H4: 6,
					H5: 7,
					TY: 8,
					STY: 9,
				}
				const index = categoryMap[stormCat]
				if (index === undefined) return
				return legendLabels['tropical-cyclones'][index]?.color
			}
			ctrl.setPaintProperty(getMapId('tropical-cyclones-track-points-archive'), 'fill.color', cycloneColor)
			ctrl.setPaintProperty(getMapId('tropical-cyclones-positions'), 'fill.color', cycloneColor)
			ctrl.setPaintProperty(getMapId('tropical-cyclones-forecast-points'), 'fill.color', cycloneColor)
		},
	}),
}
const temperature: LayerView = {
	id: 'temperatures',
	type: 'mapsgl',
	layers: [{ id: 'temperatures' }],
	title: <I18n i18nKey={($) => $.layers.views.temperature} />,
	animatable: true,
	layerStyle: (pref, theme) => {
		const colorStops = {
			DEGREES_CELSIUS: interpolateColorscaleFlat(colorscales.temperatures[theme], ...legendRanges.temperatures.DEGREES_CELSIUS),
			DEGREES_FAHRENHEIT: interpolateColorscaleFlat(
				colorscales.temperatures[theme],
				...legendRanges.temperatures.DEGREES_FAHRENHEIT,
				getConversionFunction('AIR_TEMPERATURE', 'DEGREES_CELSIUS', 'DEGREES_FAHRENHEIT'),
			),
		}[pref.unitsAirTemperature]
		return {
			type: 'gradient',
			unit: pref.unitsAirTemperature,
			min: legendRanges.temperatures[pref.unitsAirTemperature][0],
			max: legendRanges.temperatures[pref.unitsAirTemperature][1],
			colorStops: colorStops,
			updateMapsglLayer: (ctrl) => ctrl.setPaintProperty(getMapId('temperatures'), 'sample.colorscale.stops', colorStops),
		}
	},
}
const feelsLike: LayerView = {
	id: 'feels-like',
	type: 'mapsgl',
	layers: [{ id: 'feels-like' }],
	title: <I18n i18nKey={($) => $.layers.views['feels-like']} />,
	animatable: true,
	layerStyle: (pref, theme) => {
		const colorStops = {
			DEGREES_CELSIUS: interpolateColorscaleFlat(colorscales.temperatures[theme], ...legendRanges.temperatures.DEGREES_CELSIUS),
			DEGREES_FAHRENHEIT: interpolateColorscaleFlat(
				colorscales.temperatures[theme],
				...legendRanges.temperatures.DEGREES_FAHRENHEIT,
				getConversionFunction('AIR_TEMPERATURE', 'DEGREES_CELSIUS', 'DEGREES_FAHRENHEIT'),
			),
		}[pref.unitsAirTemperature]
		return {
			type: 'gradient',
			unit: pref.unitsAirTemperature,
			min: legendRanges.temperatures[pref.unitsAirTemperature][0],
			max: legendRanges.temperatures[pref.unitsAirTemperature][1],
			colorStops: colorStops,
			updateMapsglLayer: (ctrl) => ctrl.setPaintProperty(getMapId('feels-like'), 'sample.colorscale.stops', colorStops),
		}
	},
}
const dewPoints: LayerView = {
	id: 'dew-points',
	title: <I18n i18nKey={($) => $.layers.views['dew-points']} />,
	type: 'mapsgl',
	layers: [{ id: 'dew-points' }],
	animatable: true,
	layerStyle: (pref, theme) => {
		const colorStops = {
			DEGREES_CELSIUS: interpolateColorscaleFlat(colorscales.temperatures[theme], ...legendRanges.temperatures.DEGREES_CELSIUS),
			DEGREES_FAHRENHEIT: interpolateColorscaleFlat(
				colorscales.temperatures[theme],
				...legendRanges.temperatures.DEGREES_FAHRENHEIT,
				getConversionFunction('AIR_TEMPERATURE', 'DEGREES_CELSIUS', 'DEGREES_FAHRENHEIT'),
			),
		}[pref.unitsAirTemperature]
		return {
			type: 'gradient',
			unit: pref.unitsAirTemperature,
			min: legendRanges.temperatures[pref.unitsAirTemperature][0],
			max: legendRanges.temperatures[pref.unitsAirTemperature][1],
			colorStops: colorStops,
			updateMapsglLayer: (ctrl) => ctrl.setPaintProperty(getMapId('dew-points'), 'sample.colorscale.stops', colorStops),
		}
	},
}
const radar: LayerView = {
	id: 'radar',
	title: <I18n i18nKey={($) => $.layers.views.radar} />,
	type: 'mapsgl',
	layers: [{ id: 'radar', options: () => ({ data: { maxZoom: 12 } }) }],
	animatable: true,
	layerStyle: () => {
		const colorStopsRain = interpolateColorscaleFlat(colorscales['radar-rain'], ...legendRanges['radar-rain'])
		const colorStopsMix = interpolateColorscaleFlat(colorscales['radar-mix'], ...legendRanges['radar-mix'])
		const colorStopsSnow = interpolateColorscaleFlat(colorscales['radar-snow'], ...legendRanges['radar-snow'])
		return {
			type: 'multiple',
			unit: 'DECIBELS_OF_REFLECTIVITY',
			styles: [
				{
					id: 'radar-rain',
					type: 'gradient',
					title: <I18n i18nKey={($) => $.legend.captions.radar.radarRain} />,
					min: legendRanges['radar-rain'][0],
					max: legendRanges['radar-rain'][1],
					colorStops: colorStopsRain,
				},
				{
					id: 'radar-mix',
					type: 'gradient',
					title: <I18n i18nKey={($) => $.legend.captions.radar.radarMix} />,
					min: legendRanges['radar-mix'][0],
					max: legendRanges['radar-mix'][1],
					colorStops: colorStopsMix,
				},
				{
					id: 'radar-snow',
					type: 'gradient',
					title: <I18n i18nKey={($) => $.legend.captions.radar.radarSnow} />,
					min: legendRanges['radar-snow'][0],
					max: legendRanges['radar-snow'][1],
					colorStops: colorStopsSnow,
				},
			],
			updateMapsglLayer: (ctrl) =>
				ctrl.setPaintProperty(getMapId('radar'), 'sample.colorscale', {
					normalized: false,
					masks: [
						// rain
						{ colorscale: { stops: colorStopsRain } },
						// mix
						{ colorscale: { stops: colorStopsMix } },
						// snow
						{ colorscale: { stops: colorStopsSnow } },
					],
				}),
		}
	},
}
const satelliteInfrared: LayerView = {
	id: 'satellite-infrared-color',
	title: <I18n i18nKey={($) => $.layers.views['satellite-infrared-color']} />,
	type: 'mapsgl',
	layers: [{ id: 'satellite-infrared-color', options: () => ({ data: { maxZoom: 10 } }) }],
	animatable: false,
	layerStyle: (pref) => {
		return {
			type: 'gradient',
			unit: pref.unitsAirTemperature,
			min: legendRanges['satellite-infrared-color'][pref.unitsAirTemperature][0],
			max: legendRanges['satellite-infrared-color'][pref.unitsAirTemperature][1],
			format: (v) => Math.round(v),
			colorStops: {
				DEGREES_CELSIUS: interpolateColorscaleFlat(
					colorscales['satellite-infrared-color'],
					...legendRanges['satellite-infrared-color'].DEGREES_CELSIUS,
				),
				DEGREES_FAHRENHEIT: interpolateColorscaleFlat(
					colorscales['satellite-infrared-color'],
					...legendRanges['satellite-infrared-color'].DEGREES_FAHRENHEIT,
				),
			}[pref.unitsAirTemperature],
			updateMapsglLayer: () => {}, // raster layer doesn't take in custom colors
		}
	},
}
const snow: LayerView = {
	id: 'snow',
	title: <I18n i18nKey={($) => $.layers.views.snow} />,
	type: 'mapsgl',
	layers: [{ id: 'snow' }],
	animatable: true,
	layerStyle: (pref, theme) => {
		const colorStops = {
			MILLIMETRES: interpolateColorscaleFlat(colorscales.snow[theme], ...legendRanges.snow.MILLIMETRES),
			INCHES: interpolateColorscaleFlat(
				colorscales.snow[theme],
				...legendRanges.snow.INCHES,
				getConversionFunction('SNOW_DEPTH', 'METRES', 'INCHES'),
			),
		}[pref.unitsRain]
		const [min, max] = legendRanges.snow[pref.unitsRain]
		const mid = (Math.round(min) + Math.round(max)) / 2
		return {
			type: 'gradient',
			unit: pref.unitsRain === 'MILLIMETRES' ? 'CENTIMETRES' : 'INCHES',
			// Mid tick is the midpoint of the rounded min/max, with 1 decimal if needed, so it stays centred between them.
			format: (v) => {
				if (pref.unitsRain === 'MILLIMETRES') return Math.round(v * 100)
				return v === mid ? +v.toFixed(1) : Math.round(v)
			},
			min,
			max,
			// INCHES' range is too narrow for the default 5-tick split to round to distinct whole numbers.
			ticks: pref.unitsRain === 'INCHES' ? minMidMaxTicks(min, max, mid) : undefined,
			colorStops: colorStops,
			updateMapsglLayer: (ctrl) => ctrl.setPaintProperty(getMapId('snow'), 'sample.colorscale.stops', colorStops),
		}
	},
}
const lightningDensity = {
	id: 'lightning-density',
	title: <I18n i18nKey={($) => $.layers.views['lightning-density']} />,
	type: 'custom',
	animatable: false,
	layers: [
		{
			id: 'lightning-density',
			render: ({ isVisible, theme, pref }) => (
				<LightningDensityLayer key='lightning-density' isVisible={isVisible} pref={pref} theme={theme} />
			),
		},
	],
	layerStyle: (pref, theme = 'light') => {
		const colorStops = segmentColorscale(colorscales['lightning-density'][theme])
		return {
			type: 'step',
			steps: [...Object.keys(colorscales['lightning-density'][theme]), Infinity],
			format: (v) => (v === Infinity ? '∞' : (v * legendRanges['lightning-density'][pref.unitsDistance][1]).toFixed(0)),
			unit: eventsPerYearLongUnit[pref.unitsDistance],
			colorStops: colorStops,
		}
	},
} as const satisfies LayerView
const lightningRealtime = {
	id: 'lightning-realtime',
	title: <I18n i18nKey={($) => $.layers.views['lightning-realtime']} />,
	type: 'custom',
	animatable: true,
	layers: [
		{
			id: 'lightning-realtime-grid',
			render: ({ isVisible, theme }) => <LightningGridLayer key='lightning-realtime-grid' isVisible={isVisible} theme={theme} />,
			animate: ({ isVisible, theme }) => <LightningGridAnimated key='lightning-realtime-grid' isVisible={isVisible} theme={theme} />,
		},
		{
			id: 'lightning-realtime-point',
			render: ({ isVisible, theme }) => <LightningPointLayer key='lightning-realtime-point' isVisible={isVisible} theme={theme} />,
			animate: ({ isVisible, theme }) => <LightningPointAnimated key='lightning-realtime-point' isVisible={isVisible} theme={theme} />,
		},
	],
	layerStyle: (_pref, theme = 'light', inputs, mode = 'light') => {
		const { cloudToGround } = inputs['lightning-realtime']
		return {
			type: 'multiple',
			unit: 'MINUTES',
			styles: [
				{
					id: 'age',
					type: 'step',
					steps: [...Object.keys(colorscales['lightning-realtime'][theme]), 1].reverse(),
					format: (v) => (-v * inputs['lightning-realtime'].maxAge).toFixed(0),
					colorStops: segmentColorscale(reverseColorscale(colorscales['lightning-realtime'][theme])),
				},
				{
					id: 'eventType',
					type: 'icon',
					title: <I18n i18nKey={($) => $.legend.captions.lightning.eventTypeTitle} />,
					icons: cloudToGround
						? legendLabels['legend.captions.lightning.cloudToGround'][mode]
						: legendLabels['legend.captions.lightning.eventTypeTitle'][mode],
					useShouldHide: () => useIsMapZoomBelow(LIGHTNING_ZOOM_BOUNDARY),
				},
			],
		}
	},
	LayerInputComponent: () => {
		const { maxAge, cloudToGround, set } = useLayerInput('lightning-realtime')
		// TODO: remove animate check when layer is fully animated
		const isPlaying = useTimeline((s) => s.isPlaying)
		useEffect(() => {
			if (isPlaying && cloudToGround) {
				set({ maxAge, cloudToGround: false })
			}
		}, [isPlaying, cloudToGround, maxAge, set])
		return (
			<>
				<ToggleButtonInput
					options={[
						{ value: 60, label: <I18n i18nKey={($) => $.legend.inputs.lightning.last60Min} />, key: 'last60Min' },
						{ value: 30, label: <I18n i18nKey={($) => $.legend.inputs.lightning.last30Min} />, key: 'last30Min' },
					]}
					selected={maxAge}
					onChange={(v) => set({ maxAge: v, cloudToGround })}
				/>
				<ToggleButtonInput
					options={[
						{ value: false, label: <I18n i18nKey={($) => $.legend.inputs.lightning.all} />, key: 'all' },
						{
							value: true,
							label: <I18n i18nKey={($) => $.legend.inputs.lightning.cloudToGround} />,
							disabled: isPlaying,
							key: 'cloudToGround',
						},
					]}
					selected={cloudToGround}
					onChange={(v) => set({ maxAge, cloudToGround: isPlaying ? false : v })}
				/>
			</>
		)
	},
} as const satisfies LayerView
const lightningAlertRings = {
	id: 'lightning-alert-rings',
	title: <I18n i18nKey={($) => $.layers.views['lightning-alert-rings']} />,
	animatable: true,
	type: 'custom',
	layers: [
		{
			id: 'lightning-alert-rings',
			render: ({ isVisible, theme }) => <LightningAlertRings key='lightning-alert-rings' isVisible={isVisible} theme={theme} />,
		},
	],
	layerStyle: (_pref) => ({
		type: 'icon',
		icons: legendLabels['lightning-alert-rings'],
	}),
} as const satisfies LayerView
const lightningThreatZone = {
	id: 'lightning-threatzone',
	title: <I18n i18nKey={($) => $.layers.views['lightning-threatzone']} />,
	animatable: false,
	type: 'custom',
	layers: [
		{
			id: 'ltz-polygon',
			render: ({ isVisible, theme }) => <LTZPolygonLayer key='ltz-polygon' isVisible={isVisible} theme={theme} />,
		},
		{
			id: 'ltz-centroid',
			render: ({ isVisible, theme }) => <LTZCircleLayer key='ltz-centroid' isVisible={isVisible} theme={theme} />,
		},
		{
			id: 'ltz-velocity',
			render: ({ isVisible, theme }) => <LTZLineLayer key='ltz-velocity' isVisible={isVisible} theme={theme} />,
		},
	],
	layerStyle: (_pref, theme = 'light') => ({
		type: 'step',
		steps: [...Object.keys(colorscales['lightning-threatzone'][theme]).sort((a, b) => Number(a) - Number(b))],
		format: (v) => (v * 60).toFixed(0),
		unit: 'MINUTES',
		colorStops: segmentColorscale(colorscales['lightning-threatzone'][theme]),
	}),
} as const satisfies LayerView

const hailThreats: LayerView = {
	id: 'hail-threats',
	title: <I18n i18nKey={($) => $.layers.views['hail-threats']} />,
	animatable: true,
	type: 'mapsgl',
	layers: [
		{
			id: 'hail-threats-polygons',
			options: () => ({
				data: { maxZoom: 10 },
				minZoom: LTZ_DETAILED_MIN_ZOOM,
				filter: ['<=', ['map-time'], ['+', ['now'], 60]],
			}),
		},
		{
			id: 'hail-threats-tracks',
			options: () => ({
				data: { maxZoom: 10 },
				minZoom: LTZ_DETAILED_MIN_ZOOM,
				filter: ['all', ['==', 'featureType', 'forecastPath'], ['<=', ['map-time'], ['+', ['now'], 60]]],
			}),
		},
		{
			id: 'hail-threats-points',
			options: () => ({ data: { maxZoom: 10 }, filter: ['<=', ['map-time'], ['+', ['now'], 60]] }),
		},
	],
	layerStyle: (_pref, theme = 'light') => {
		const colorStops = colorscales['hail-threats'][theme]
		const hailColor = (data: { details: { issuedTimestamp: number }; period: { range: { minTimestamp: number } } }) => {
			const { details, period } = data
			if (!details || !period) return '#000000'
			const leadTimeMinutes = (period.range.minTimestamp - details.issuedTimestamp) / legendRanges['hail-threats'][1]
			const interval = Number.parseFloat((Math.max(0, Math.floor(leadTimeMinutes / 10) * 10) / legendRanges['hail-threats'][1]).toFixed(3))
			return colorStops[interval as keyof typeof colorStops] ?? '#000000'
		}
		const pointOpacity = (data: { period?: { range?: { minTimestamp: number; maxTimestamp: number } } }) => {
			const range = data.period?.range
			if (!range) return 0
			return range.minTimestamp === range.maxTimestamp ? 1 : 0
		}
		return {
			type: 'step',
			steps: [...Object.keys(colorStops).sort((a, b) => Number(a) - Number(b))],
			format: (v) => (v * legendRanges['hail-threats'][1]).toFixed(0),
			unit: 'MINUTES',
			colorStops: segmentColorscale(colorStops),
			updateMapsglLayer: (ctrl) => {
				ctrl.setPaintProperty(getMapId('hail-threats-polygons'), 'fill.color', hailColor)
				ctrl.setPaintProperty(getMapId('hail-threats-polygons'), 'fill.opacity', 0.8)
				ctrl.setPaintProperty(getMapId('hail-threats-tracks'), 'stroke.color', colorStops[1])
				ctrl.setPaintProperty(getMapId('hail-threats-points'), 'circle.radius', 5)
				ctrl.setPaintProperty(getMapId('hail-threats-points'), 'fill.opacity', pointOpacity)
				ctrl.setPaintProperty(getMapId('hail-threats-points'), 'stroke.opacity', pointOpacity)
				ctrl.setPaintProperty(getMapId('hail-threats-points'), 'fill.color', colorStops[1])
			},
		}
	},
}

const temperatures = {
	type: 'switch',
	id: 'temperatures',
	title: <I18n i18nKey={($) => $.layers.views.temperature} />,
	layerViews: [temperature, feelsLike],
} as const satisfies LayerView
const temperatureOrDewPoints = {
	type: 'switch',
	id: 'temperatures',
	title: <I18n i18nKey={($) => $.layers.views.temperature} />,
	layerViews: [temperature, dewPoints],
} as const satisfies LayerView
const precip = {
	type: 'switch',
	id: 'precip',
	title: <I18n i18nKey={($) => $.layers.views.precip} />,
	layerViews: [radar, satelliteInfrared, snow],
} as const satisfies LayerView
const extremePhenomena = {
	type: 'switch',
	id: 'extreme',
	title: <I18n i18nKey={($) => $.layers.views.extreme} />,
	layerViews: [wildfires, tropicalCyclones],
} as const satisfies LayerView

const surfacePressure = {
	type: 'mapsgl',
	id: 'pressure-msl',
	title: <I18n i18nKey={($) => $.layers.views['pressure-msl']} />,
	animatable: true,
	layers: [{ id: 'pressure-msl-contour' }],
	layerStyle: (pref, theme) => {
		const colorStops = {
			HECTOPASCALS: interpolateColorscaleFlat(colorscales['pressure-msl'][theme], ...legendRanges['pressure-msl'].HECTOPASCALS),
			MILLIBARS: interpolateColorscaleFlat(colorscales['pressure-msl'][theme], ...legendRanges['pressure-msl'].MILLIBARS),
			INCH_OF_MERCURY: interpolateColorscaleFlat(
				colorscales['pressure-msl'][theme],
				...legendRanges['pressure-msl'].INCH_OF_MERCURY,
				getConversionFunction('AIR_PRESSURE', 'HECTOPASCALS', 'INCH_OF_MERCURY'),
			),
		}[pref.unitsAirPressure]
		const [min, max] = legendRanges['pressure-msl'][pref.unitsAirPressure]
		const mid = (Math.round(min) + Math.round(max)) / 2
		return {
			type: 'gradient',
			unit: pref.unitsAirPressure,
			min,
			max,
			// Mid tick is the midpoint of the rounded min/max, with 1 decimal if needed, so it stays centred between them.
			format: (v) => (pref.unitsAirPressure === 'INCH_OF_MERCURY' && v === mid ? +v.toFixed(1) : Math.round(v)),
			// INCH_OF_MERCURY's range is too narrow for the default 5-tick split to round to distinct whole numbers.
			ticks: pref.unitsAirPressure === 'INCH_OF_MERCURY' ? minMidMaxTicks(min, max, mid) : undefined,
			colorStops: colorStops,
			updateMapsglLayer: (ctrl) => ctrl.setPaintProperty(getMapId('pressure-msl-contour'), 'sample.colorscale.stops', colorStops),
		}
	},
} as const satisfies LayerView
const windParticles = {
	type: 'mapsgl',
	id: 'wind-particles',
	title: <I18n i18nKey={($) => $.layers.views.wind} />,
	animatable: true,
	layers: [{ id: 'wind-particles' }],
	layerStyle: (pref, theme) => {
		const colorStops = {
			METRES_PER_SECOND: interpolateColorscaleFlat(
				colorscales['wind-particles'][theme],
				...legendRanges['wind-particles'].METRES_PER_SECOND,
			),
			MILES_PER_HOUR: interpolateColorscaleFlat(
				colorscales['wind-particles'][theme],
				...legendRanges['wind-particles'].MILES_PER_HOUR,
				getConversionFunction('WIND_SPEED', 'METRES_PER_SECOND', 'MILES_PER_HOUR'),
			),
			KNOTS: interpolateColorscaleFlat(
				colorscales['wind-particles'][theme],
				...legendRanges['wind-particles'].MILES_PER_HOUR,
				getConversionFunction('WIND_SPEED', 'METRES_PER_SECOND', 'KNOTS'),
			),
			KILOMETRES_PER_HOUR: interpolateColorscaleFlat(
				colorscales['wind-particles'][theme],
				...legendRanges['wind-particles'].MILES_PER_HOUR,
				getConversionFunction('WIND_SPEED', 'METRES_PER_SECOND', 'KILOMETRES_PER_HOUR'),
			),
		}[pref.unitsWindSpeed]
		return {
			type: 'gradient',
			unit: pref.unitsWindSpeed,
			min: legendRanges['wind-particles'][pref.unitsWindSpeed][0],
			max: legendRanges['wind-particles'][pref.unitsWindSpeed][1],
			format: (v) => Math.round(v),
			colorStops: colorStops,
			updateMapsglLayer: (ctrl) => {
				const mapId = getMapId('wind-particles')
				ctrl.setPaintProperty(mapId, 'particle.count', 128)
				ctrl.setPaintProperty(mapId, 'particle.size', 1.5)
				ctrl.setPaintProperty(mapId, 'particle.trailsFadeFactor', 0.95)
				ctrl.setPaintProperty(mapId, 'particle.speedFactor', 2.5)
				ctrl.setPaintProperty(mapId, 'sample.colorscale.stops', colorStops)
			},
		}
	},
} as const satisfies LayerView
const windGusts = {
	type: 'mapsgl',
	id: 'wind-gusts',
	title: <I18n i18nKey={($) => $.layers.views['wind-gusts']} />,
	animatable: true,
	layers: [{ id: 'wind-gusts' }],
	layerStyle: (pref, theme) => {
		const colorStops = {
			METRES_PER_SECOND: interpolateColorscaleFlat(colorscales['wind-gusts'][theme], ...legendRanges['wind-gusts'].METRES_PER_SECOND),
			MILES_PER_HOUR: interpolateColorscaleFlat(
				colorscales['wind-gusts'][theme],
				...legendRanges['wind-gusts'].MILES_PER_HOUR,
				getConversionFunction('WIND_SPEED', 'METRES_PER_SECOND', 'MILES_PER_HOUR'),
			),
			KNOTS: interpolateColorscaleFlat(
				colorscales['wind-gusts'][theme],
				...legendRanges['wind-gusts'].MILES_PER_HOUR,
				getConversionFunction('WIND_SPEED', 'METRES_PER_SECOND', 'KNOTS'),
			),
			KILOMETRES_PER_HOUR: interpolateColorscaleFlat(
				colorscales['wind-gusts'][theme],
				...legendRanges['wind-gusts'].MILES_PER_HOUR,
				getConversionFunction('WIND_SPEED', 'METRES_PER_SECOND', 'KILOMETRES_PER_HOUR'),
			),
		}[pref.unitsWindSpeed]
		return {
			unit: pref.unitsWindSpeed,
			type: 'gradient',
			min: legendRanges['wind-gusts'][pref.unitsWindSpeed][0],
			max: legendRanges['wind-gusts'][pref.unitsWindSpeed][1],
			format: (v) => Math.round(v),
			colorStops: colorStops,
			updateMapsglLayer: (ctrl) => ctrl.setPaintProperty(getMapId('wind-gusts'), 'sample.colorscale.stops', colorStops),
		}
	},
} as const satisfies LayerView
const airQualityIndex = {
	type: 'mapsgl',
	id: 'air-quality-index',
	title: <I18n i18nKey={($) => $.layers.views['air-quality-index']} />, // TODO: may replace with air-quality-index-categories for AirNow and air-quality-index-eaqi-categories for EAQI
	animatable: true,
	layers: [{ id: 'air-quality-index' }],
	layerStyle: (_pref, theme) => {
		const colorStops = interpolateColorscaleFlat(colorscales['air-quality-index'][theme], ...legendRanges['air-quality-index'])
		return {
			type: 'gradient',
			unit: 'AIR_QUALITY_INDEX',
			min: legendRanges['air-quality-index'][0],
			max: legendRanges['air-quality-index'][1],
			colorStops: colorStops,
			updateMapsglLayer: (ctrl) => ctrl.setPaintProperty(getMapId('air-quality-index'), 'sample.colorscale.stops', colorStops),
		}
	},
} as const satisfies LayerView
const visibility = {
	type: 'mapsgl',
	id: 'visibility',
	title: <I18n i18nKey={($) => $.layers.views.visibility} />,
	animatable: true,
	layers: [{ id: 'visibility' }],
	layerStyle: (pref, theme) => {
		const colorStops = {
			KILOMETRES: interpolateColorscaleFlat(
				colorscales.visibility[theme],
				...legendRanges.visibility.KILOMETRES,
				getConversionFunction('DISTANCE', 'METRES', 'KILOMETRES'),
			),
			METRES: interpolateColorscaleFlat(colorscales.visibility[theme], ...legendRanges.visibility.METRES),
			MILES: interpolateColorscaleFlat(
				colorscales.visibility[theme],
				...legendRanges.visibility.MILES,
				getConversionFunction('DISTANCE', 'METRES', 'MILES'),
			),
			NAUTICAL_MILES: interpolateColorscaleFlat(
				colorscales.visibility[theme],
				...legendRanges.visibility.NAUTICAL_MILES,
				getConversionFunction('DISTANCE', 'METRES', 'NAUTICAL_MILES'),
			),
		}[pref.unitsDistance]
		return {
			type: 'gradient',
			unit: pref.unitsDistance,
			min: legendRanges.visibility[pref.unitsDistance][0],
			max: legendRanges.visibility[pref.unitsDistance][1],
			format: (v) => Math.round(v),
			colorStops: colorStops,
			updateMapsglLayer: (ctrl) => ctrl.setPaintProperty(getMapId('visibility'), 'sample.colorscale.stops', colorStops),
		}
	},
} as const satisfies LayerView
const roadTemperature = {
	type: 'mapsgl',
	id: 'road-temperature',
	title: <I18n i18nKey={($) => $.layers.views['road-temperature']} />,
	animatable: false,
	layers: [{ id: 'froad-weather-temperature-us' }, { id: 'froad-weather-temperature-europe' }],
	layerStyle: (pref) => {
		const colorStops = [
			-40,
			'#a50099',
			-34.4,
			'#ed00dc',
			-28.8,
			'#f671ff',
			-23.3,
			'#dcc1ed',
			-17.7,
			'#1b007b',
			-12.2,
			'#6e51ff',
			-6.6,
			'#9bb6ff',
			-1.1,
			'#18a5fc',
			4.4,
			'#0900e7',
			10,
			'#14b000',
			15.5,
			'#f5d508',
			21.1,
			'#e17200',
			26.6,
			'#f33300',
			32.2,
			'#d00000',
			37.7,
			'#720000',
			43.3,
			'#ce0046',
			48.8,
			'#ff4b98',
			54.4,
			'#ffadad',
			60,
			'#9f0000',
			65.5,
			'#ca4931',
			70,
			'#e9a696',
		]
		return {
			type: 'gradient',
			min: -40,
			max: 70,
			unit: pref.unitsAirTemperature,
			// TODO: This only affects the legend, not the actual layer
			format: (v) => Math.round(v),
			colorStops: colorStops,
		}
	},
} as const satisfies LayerView
const roadWeatherSummary = {
	type: 'mapsgl',
	id: 'road-weather-summary',
	title: <I18n i18nKey={($) => $.layers.views['road-weather-summary']} />,
	animatable: false,
	layers: [
		{ id: 'froad-weather-summary-us' },
		{ id: 'froad-weather-summary-europe' },
		{ id: 'froad-weather-summary-australia' },
		{ id: 'froad-weather-summary-japan' },
		{ id: 'froad-weather-summary-new-zealand' },
	],
	layerStyle: (_pref) => ({
		type: 'category',
		categories: legendLabels['road-weather-summary'],
		colorStops: categorizeColorscale(legendLabels['road-weather-summary']),
		updateMapsglLayer: (ctrl) => {
			const roadColor = (roadData: { 'roadSeverity_0-SFC': string }) =>
				({
					GREEN: legendLabels['road-weather-summary'][2].color,
					YELLOW: legendLabels['road-weather-summary'][1].color,
					RED: legendLabels['road-weather-summary'][0].color,
				})[roadData['roadSeverity_0-SFC'] as string] ?? '#000000'
			ctrl.setPaintProperty(getMapId('froad-weather-summary-us'), 'fill.color', roadColor)
			ctrl.setPaintProperty(getMapId('froad-weather-summary-europe'), 'fill.color', roadColor)
		},
	}),
} as const satisfies LayerView
const snowDepth = {
	type: 'mapsgl',
	id: 'snow-depth',
	title: <I18n i18nKey={($) => $.layers.views['snow-depth']} />,
	layers: [{ id: 'snow-depth' }],
	animatable: true,
	layerStyle: (pref, theme) => {
		const colorStops = {
			MILLIMETRES: interpolateColorscaleFlat(colorscales.snow[theme], ...legendRanges.snow.MILLIMETRES),
			INCHES: interpolateColorscaleFlat(
				colorscales.snow[theme],
				...legendRanges.snow.INCHES,
				getConversionFunction('SNOW_DEPTH', 'METRES', 'INCHES'),
			),
		}[pref.unitsRain]
		const [min, max] = legendRanges.snow[pref.unitsRain]
		const mid = (Math.round(min) + Math.round(max)) / 2
		return {
			type: 'gradient',
			min,
			max,
			// Mid tick is the midpoint of the rounded min/max, with 1 decimal if needed, so it stays centred between them.
			format: (v) => {
				if (pref.unitsRain === 'MILLIMETRES') return Math.round(v * 100)
				return v === mid ? +v.toFixed(1) : Math.round(v)
			},
			// INCHES' range is too narrow for the default 5-tick split to round to distinct whole numbers.
			ticks: pref.unitsRain === 'INCHES' ? minMidMaxTicks(min, max, mid) : undefined,
			unit: pref.unitsRain === 'MILLIMETRES' ? 'CENTIMETRES' : 'INCHES',
			colorStops: colorStops,
			updateMapsglLayer: (ctrl) => ctrl.setPaintProperty(getMapId('snow-depth'), 'sample.colorscale.stops', colorStops),
		}
	},
} as const satisfies LayerView

export const layerViews: LayerView[] = [
	airQualityIndex,
	extremePhenomena,
	govtAlerts,
	lightningAlertRings,
	lightningDensity,
	powerLines,
	lightningRealtime,
	precip,
	roadTemperature,
	roadWeatherSummary,
	snowDepth,
	surfacePressure,
	temperatureOrDewPoints,
	temperatures,
	tropicalCyclones,
	visibility,
	wildfires,
	windGusts,
	windParticles,
]

export const allLayerGroups = {
	severe: {
		title: <I18n i18nKey={($) => $.layers.groups.severe} />,
		layerViews: [lightningAlertRings, lightningRealtime, hailThreats, lightningThreatZone, extremePhenomena, govtAlerts],
		defaultLayers: [lightningAlertRings.id, lightningRealtime.id, lightningThreatZone.id],
	},
	general: {
		title: <I18n i18nKey={($) => $.layers.groups.general} />,
		layerViews: [windParticles, precip, surfacePressure, temperatures],
		defaultLayers: [],
	},
	wind: {
		title: <I18n i18nKey={($) => $.layers.groups.wind} />,
		layerViews: [windParticles, windGusts],
		defaultLayers: [windParticles.id],
	},
	airQuality: {
		title: <I18n i18nKey={($) => $.layers.groups.airQuality} />,
		layerViews: [windParticles, visibility, airQualityIndex],
		defaultLayers: [airQualityIndex.id],
	},
	road: {
		title: <I18n i18nKey={($) => $.layers.groups.road} />,
		layerViews: [roadWeatherSummary, temperatureOrDewPoints],
		defaultLayers: [roadWeatherSummary.id],
	},
	climatology: {
		title: <I18n i18nKey={($) => $.layers.groups.climatology} />,
		layerViews: [lightningDensity, powerLines],
		defaultLayers: [lightningDensity.id],
	},
} as const satisfies Record<string, LayerGroup>

export type LayerGroupId = keyof typeof allLayerGroups
interface LayerGroup {
	title: ReactNode
	layerViews: LayerView[]
	defaultLayers: LayerViewId[]
}

export const useLayerGroups = () => {
	const { data: subscription } = useSubscription()
	const { ltzAlertsV2 } = useFlags()
	const layerGroups = useMemo(
		() =>
			Object.entries(allLayerGroups)
				.map(([id, group]) => ({
					id,
					...group,
					layerViews: group.layerViews.map((layerView) =>
						layerView.id === lightningThreatZone.id && ltzAlertsV2 ? { ...lightningThreatZone, animatable: true } : layerView,
					),
				}))
				.filter((layerGroup) => subscription.layerGroups.includes(layerGroup.id as LayerGroupId)),
		[subscription, ltzAlertsV2],
	)
	return layerGroups as (LayerGroup & { id: LayerGroupId })[]
}
